// A7 — Integración de planta (admin_tenant y super_admin). Contrato de
// ingesta §9: usuario técnico, credenciales, mapeos, fuente de captura por
// línea, peso por SKU y parámetros. Arriba, la puesta en marcha en orden.

import { Navigate } from 'react-router-dom';
import { Icon } from '../../../components/ui/icon';
import { Spinner } from '../../../components/ui/spinner';
import { AdminSidebar } from '../../../components/layout/AdminSidebar';
import { RoleLayout } from '../../../components/layout/RoleLayout';
import {
  useCredencialesIngesta,
  useIngestaConfig,
  useMapeosIngesta,
} from '../../../lib/api/ingesta';
import { useAdminCausales, useAdminEstructura } from '../../../lib/api/queries';
import { useAuth } from '../../../lib/auth';
import { cn } from '../../../lib/utils';
import { mensajeError } from './comun';
import {
  causalesEspecificas,
  LineasSeccion,
  lineasDeEstructura,
  MapeosSeccion,
  ParametrosSeccion,
  PesoSkuSeccion,
} from './ConfiguracionSecciones';
import { CredencialesSeccion, UsuarioTecnicoSeccion } from './CredencialesSeccion';

export function A7IntegracionPlantaPage() {
  const { state } = useAuth();
  const config = useIngestaConfig();
  const credenciales = useCredencialesIngesta();
  const mapeos = useMapeosIngesta();
  const estructura = useAdminEstructura();
  const causalesQ = useAdminCausales();

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const lineas = lineasDeEstructura(estructura.data);
  const causales = causalesEspecificas(causalesQ.data);
  const cfg = config.data;
  const tipos = new Set((mapeos.data ?? []).map((m) => m.tipo));
  const pasos: { titulo: string; hecho: boolean }[] = [
    { titulo: 'Usuario técnico', hecho: Boolean(cfg?.usuario_tecnico_id) },
    { titulo: 'Credencial activa', hecho: (credenciales.data ?? []).some((c) => c.activa) },
    { titulo: 'Mapeo de líneas y SKUs', hecho: tipos.has('linea') && tipos.has('sku') },
    { titulo: 'Causal pendiente', hecho: Boolean(cfg?.causal_pendiente_id) },
  ];

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-4">
          <div>
            <h1 className="text-xl font-medium text-fg">Integración de planta</h1>
            <p className="mt-1 text-[12px] text-fg-tertiary">
              Conexión de las máquinas de planta con JIDOKAP: quién envía datos, cómo se traducen
              sus códigos y qué líneas capturan solas.
            </p>
          </div>
          <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Puesta en marcha">
            {pasos.map((p, i) => (
              <li
                key={p.titulo}
                className={cn(
                  'flex items-center gap-2 rounded border px-3 py-2 text-[12px]',
                  p.hecho
                    ? 'border-ok/30 bg-ok-soft text-ok'
                    : 'border-border bg-surface-elevated text-fg-secondary',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px]',
                    p.hecho ? 'bg-ok text-surface-base' : 'border border-border-strong',
                  )}
                >
                  {p.hecho ? <Icon name="check" size={12} stroke={2.5} /> : i + 1}
                </span>
                {p.titulo}
              </li>
            ))}
          </ol>
        </header>

        {config.isLoading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-fg-secondary">
            <Spinner className="size-4" /> Cargando…
          </div>
        ) : config.isError || !cfg ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-sm text-crit">
            {mensajeError(config.error)}
          </p>
        ) : (
          <>
            <UsuarioTecnicoSeccion usuarioTecnicoId={cfg.usuario_tecnico_id} />
            <CredencialesSeccion hayUsuarioTecnico={cfg.usuario_tecnico_id !== null} />
            <MapeosSeccion lineas={lineas} causales={causales} />
            <LineasSeccion lineas={lineas} />
            <PesoSkuSeccion lineas={lineas} />
            <ParametrosSeccion config={cfg} causales={causales} />
          </>
        )}
      </div>
    </RoleLayout>
  );
}
